import * as THREE from 'three';
import { CONFIG } from '../config';
import type { RideStats } from '../content/rides';
import { clamp, damp, lerp } from '../core/math';
import type { BuiltCar } from '../world/builders/vehicles';
import type { CollisionWorld } from '../world/Collision';

export interface DriveInput {
  throttle: number;
  steer: number;
  brake: boolean;
  /** 0 on solid ground, 1 axle-deep in mud. */
  mud?: number;
}

export interface DriveResult {
  bumped: boolean;
  donut: boolean;
  drifting: boolean;
}

/**
 * Arcade car physics for the Camaro once she runs. The car faces +x locally,
 * so heading h means forward = (cos h, -sin h) on the ground plane.
 */
export class CarController {
  speed = 0;
  heading = 0;
  private spin = 0;
  private spinTimer = 0;
  private steerSmooth = 0;

  /** Handling for whatever you're driving. */
  stats: RideStats = {
    maxSpeed: CONFIG.car.maxSpeed,
    accel: CONFIG.car.accel,
    steer: CONFIG.car.steer,
    radius: CONFIG.car.radius,
    axle: 1.3,
    mud: 0.3,
    seat: 1.2,
  };

  constructor(readonly root: THREE.Object3D) {}

  get position(): THREE.Vector3 {
    return this.root.position;
  }

  forward(out = new THREE.Vector3()): THREE.Vector3 {
    return out.set(Math.cos(this.heading), 0, -Math.sin(this.heading));
  }

  reset(x: number, z: number, heading: number): void {
    this.root.position.set(x, 0, z);
    this.heading = heading;
    this.speed = 0;
    this.root.rotation.y = heading;
  }

  update(dt: number, input: DriveInput, collision: CollisionWorld, car: BuiltCar): DriveResult {
    const c = CONFIG.car;
    const st = this.stats;
    // Mud bogs down anything that wasn't built for it.
    // Even a lifted truck feels it; a Camaro just about drowns.
    const bog = (input.mud ?? 0) * (1 - 0.7 * st.mud);
    const accel = st.accel * lerp(1, 0.35, bog);
    const top = st.maxSpeed * lerp(1, 0.3, bog);
    if (input.brake) this.speed = damp(this.speed, 0, 3, dt);
    else if (input.throttle > 0) this.speed += accel * input.throttle * dt * (this.speed < 0 ? 2 : 1);
    else if (input.throttle < 0) this.speed += (this.speed > 0 ? c.brake : accel * 0.7) * input.throttle * dt;
    this.speed -= this.speed * (c.drag + bog * 1.6) * dt;
    this.speed = clamp(this.speed, -c.reverseSpeed, top);
    if (Math.abs(this.speed) < 0.05 && input.throttle === 0) this.speed = 0;

    this.steerSmooth = damp(this.steerSmooth, input.steer, 8, dt);
    const grip = clamp(Math.abs(this.speed) / 4, 0, 1);
    const yawRate = -this.steerSmooth * st.steer * grip * Math.sign(this.speed || 1);
    this.heading += yawRate * dt;

    const f = this.forward();
    const pos = this.root.position;
    const next = { x: pos.x + f.x * this.speed * dt, z: pos.z + f.z * this.speed * dt };

    // Two circles (front and back) approximate the body for collision.
    let bumped = false;
    for (const off of [st.axle, -st.axle]) {
      const p = { x: next.x + f.x * off, z: next.z + f.z * off };
      if (collision.resolve(p, st.radius, 1)) {
        next.x += p.x - (next.x + f.x * off);
        next.z += p.z - (next.z + f.z * off);
        bumped = true;
      }
    }
    if (bumped && Math.abs(this.speed) > 2) this.speed *= -0.3;
    else if (bumped) this.speed *= 0.5;
    next.x = clamp(next.x, CONFIG.bounds.minX, CONFIG.bounds.maxX);
    next.z = clamp(next.z, CONFIG.bounds.minZ, CONFIG.bounds.maxZ);
    pos.x = next.x;
    pos.z = next.z;
    this.root.rotation.y = this.heading;

    const wheelR =
      (car.wheels[0]?.geometry as THREE.CylinderGeometry | undefined)?.parameters?.radiusTop ?? 0.34;
    for (const w of car.wheels) w.rotation.y -= (this.speed * dt) / wheelR;
    car.body.rotation.x = damp(car.body.rotation.x, this.steerSmooth * grip * 0.05, 6, dt);

    // Donut detection: a full spin at speed within a few seconds.
    const drifting = Math.abs(yawRate) > 1.2 && Math.abs(this.speed) > 6;
    if (drifting) {
      this.spin += Math.abs(yawRate) * dt;
      this.spinTimer = 2.5;
    } else {
      this.spinTimer -= dt;
      if (this.spinTimer <= 0) this.spin = 0;
    }
    let donut = false;
    if (this.spin > Math.PI * 2) {
      this.spin = 0;
      donut = true;
    }
    return { bumped: bumped && Math.abs(this.speed) > 0.5, donut, drifting };
  }
}
